import api, { rawFetchWithAuth, ApiResponse } from './apiClient';
import { config } from 'config';

export type NotificationType = 'task' | 'info' | 'invitation' | 'alert' | 'welcome' | 'system';
export type NotificationPriority = 'low' | 'medium' | 'high' | 'urgent';

export interface PlatformNotification {
  _id: string;
  recipient: string;
  type: NotificationType;
  title: string;
  body: string;
  actionUrl?: string;
  relatedEntity?: {
    entityType: string;
    entityId: string;
    entityCode?: string;
  };
  priority: NotificationPriority;
  isRead: boolean;
  isArchived: boolean;
  emailSentAt?: string;
  metadata?: Record<string, any>;
  createdAt: string;
  updatedAt: string;
}

export interface PlatformNotificationsPage {
  notifications: PlatformNotification[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export const platformNotificationApi = {
  // BE nests pagination inside `data` (not the top-level `pagination`
  // envelope field) — see platformNotificationController.ts's getAll.
  getAll: async (params?: {
    page?: number;
    limit?: number;
    isRead?: boolean;
    isArchived?: boolean;
    type?: NotificationType;
  }): Promise<ApiResponse<PlatformNotificationsPage>> => {
    const queryParams: Record<string, any> = {};
    if (params?.page) queryParams.page = params.page;
    if (params?.limit) queryParams.limit = params.limit;
    if (params?.isRead !== undefined) queryParams.isRead = params.isRead;
    if (params?.isArchived !== undefined) queryParams.isArchived = params.isArchived;
    if (params?.type) queryParams.type = params.type;
    return await api.get('/platform-notifications', queryParams);
  },

  getUnreadCount: async (): Promise<number> => {
    const res: any = await api.get('/platform-notifications/unread-count');
    // BE response shape: { success, data: { count } }. Tolerate the legacy
    // `{ success, count }` shape too in case of mid-deploy version skew.
    return res?.data?.count ?? res?.count ?? 0;
  },

  markAsRead: async (id: string): Promise<void> => {
    await api.patch(`/platform-notifications/${id}/read`);
  },

  markAllAsRead: async (): Promise<void> => {
    await api.patch('/platform-notifications/read-all');
  },

  archive: async (id: string): Promise<void> => {
    await api.patch(`/platform-notifications/${id}/archive`);
  },

  archiveAllRead: async (): Promise<void> => {
    await api.patch('/platform-notifications/archive-all-read');
  },

  sendEmail: async (id: string): Promise<void> => {
    await api.post(`/platform-notifications/${id}/email`);
  },

  exportPdf: async (period: '24h' | '1w' | '1m' | '6m' | '1y'): Promise<void> => {
    const response = await rawFetchWithAuth(
      `${config.apiUrl}/platform-notifications/export?period=${period}`,
    );
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `notificaciones-${period}-${Date.now()}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  },
};
